/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { makeAutoObservable, observable } from "mobx"

import type { UserList, UserListFilter } from "./user-types"

export class UserListStore {
    users: UserList[] = []

    // All Users state
    isLoadingUsers: boolean = false
    currentPage = 0
    allUsersFilter: UserListFilter = {
        search: "",
        orgId: null,
        status: null,
        role: null,
        external: null
    }

    private readonly USERS_PER_PAGE = 50

    constructor() {
        makeAutoObservable(this, {
            isLoadingUsers: observable,
            setUsers: true,
            setLoadingUsers: true,
            setFilter: true,
            setPage: true
        })
    }

    // Observable setters
    setFilter<K extends keyof typeof this.allUsersFilter>(key: K, value: (typeof this.allUsersFilter)[K]): void {
        this.allUsersFilter[key] = value
        this.setPage(0) // reset page when filters change
    }

    resetFilters(): void {
        this.allUsersFilter = {
            search: "",
            orgId: null,
            status: null,
            role: null,
            external: null
        }
        this.setPage(0)
    }

    setPage(page: number): void {
        this.currentPage = page
    }

    setLoadingUsers(isLoading: boolean): void {
        this.isLoadingUsers = isLoading
    }

    get filteredUsers(): UserList[] {
        const { search, orgId, status, role, external } = this.allUsersFilter

        return this.users.filter((user) => {
            // Org filter
            const matchOrg = (() => {
                if (orgId === null) return true // no filter applied
                if (orgId === -1) return user.organizationId === undefined // "No organization"
                return user.organizationId === orgId // specific org match
            })()

            // Status filter: true, false, or undefined treated as false
            const matchStatus =
                status === null || (status && user.enabled === true) || (!status && user.enabled !== true)

            // Search filter
            const matchText =
                (user.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
                (user.username ?? "").toLowerCase().includes(search.toLowerCase())

            const matchRole = role === null || (user.roles?.includes(role) ?? false)
            const matchExternal = external === null || user.external === external

            return matchOrg && matchStatus && matchText && matchRole && matchExternal
        })
    }

    get totalFilteredUsers(): number {
        return this.filteredUsers.length
    }

    get pagedUsers(): UserList[] {
        const start = this.currentPage * this.USERS_PER_PAGE
        return this.filteredUsers.slice(start, start + this.USERS_PER_PAGE)
    }

    get totalPages(): number {
        return Math.ceil(this.totalFilteredUsers / this.USERS_PER_PAGE)
    }

    // Actions
    setUsers(users: UserList[]): void {
        this.users = users
    }
}
